/**
 * Convert a tutor's weekly slot (in their own IANA timezone) to the browser's
 * local timezone. Returns { weekday: 0-6, time: "HH:MM", label: "Wed 14:30" }.
 *
 * We use the CURRENT week's date for the given weekday in the tutor's tz,
 * convert to UTC, then to the student's local tz. This keeps DST honest.
 */
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Get the offset (in minutes) from UTC for a given IANA tz at a specific moment.
// Positive means EAST of UTC (e.g. Cairo = +120 for winter).
const tzOffsetMin = (isoUtc, ianaTz) => {
  const d = new Date(isoUtc);
  // formatToParts gives us the wall clock in the target tz — we compare to UTC.
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: ianaTz,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
  const parts = Object.fromEntries(dtf.formatToParts(d).map((p) => [p.type, p.value]));
  const asIfLocal = Date.UTC(
    parseInt(parts.year, 10),
    parseInt(parts.month, 10) - 1,
    parseInt(parts.day, 10),
    parseInt(parts.hour, 10) === 24 ? 0 : parseInt(parts.hour, 10),
    parseInt(parts.minute, 10),
    parseInt(parts.second, 10)
  );
  return Math.round((asIfLocal - d.getTime()) / 60000);
};

/**
 * Convert a weekly recurring slot from the tutor's tz to the student's local tz.
 *
 * @param {number} tutorWeekday  0=Mon … 6=Sun (in the tutor's tz)
 * @param {string} tutorLocalTime "HH:MM" in the tutor's tz
 * @param {string} tutorTz       IANA e.g. "Africa/Cairo"
 * @returns {{weekday:number,time:string,label:string}}
 */
export const convertSlotToLocal = (tutorWeekday, tutorLocalTime, tutorTz) => {
  try {
    const [hh, mm] = tutorLocalTime.split(":").map((s) => parseInt(s, 10));
    // Take next occurrence of `tutorWeekday` from today (JS Sunday=0 → shift so Mon=0).
    const now = new Date();
    const jsToday = (now.getDay() + 6) % 7;      // Mon=0 … Sun=6
    const daysAhead = ((tutorWeekday - jsToday) + 7) % 7;
    const anchor = new Date(now);
    anchor.setDate(now.getDate() + daysAhead);
    anchor.setHours(0, 0, 0, 0);

    // The wall clock in the tutor's tz is (yyyy-mm-dd hh:mm). Compute the equivalent UTC instant.
    const y = anchor.getFullYear(), m = anchor.getMonth() + 1, d = anchor.getDate();
    // Guess a UTC instant, then correct with the tz offset for that moment.
    const utcGuess = Date.UTC(y, m - 1, d, hh, mm, 0);
    const offset = tzOffsetMin(new Date(utcGuess).toISOString(), tutorTz);
    const utcMs = utcGuess - offset * 60000;
    const utc = new Date(utcMs);

    // Format in the browser's local timezone
    const localWeekday = (utc.getDay() + 6) % 7;  // Mon=0 … Sun=6
    const localTime = utc.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    return {
      weekday: localWeekday,
      time: localTime,
      label: `${DAYS[localWeekday]} ${localTime}`,
      utcIso: utc.toISOString(),
    };
  } catch (e) {
    return { weekday: tutorWeekday, time: tutorLocalTime, label: `${DAYS[tutorWeekday]} ${tutorLocalTime} (${tutorTz})`, utcIso: null };
  }
};

export const DAY_NAMES = DAYS;
export const localTzName = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "UTC"; }
};
